import { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const FILTERS = [
  { k: "", label: "All" },
  { k: "submitted", label: "Pending" },
  { k: "approved", label: "Approved" },
  { k: "rejected", label: "Rejected" },
  { k: "disbursed", label: "Disbursed" },
];

export default function AdminLoans({ filterStatus = "" }) {
  const [status, setStatus] = useState(filterStatus);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [decision, setDecision] = useState({ status: "approved", reason: "", approved_amount: "", interest_rate: "" });

  const load = () => {
    const url = status ? `/admin/loans?status_f=${status}` : "/admin/loans";
    api.get(url).then((r) => setRows(r.data)).catch(() => {});
  };
  useEffect(load, [status]);

  const decide = async () => {
    try {
      await api.post(`/admin/loans/${current.id}/decision`, {
        status: decision.status,
        reason: decision.reason,
        approved_amount: decision.approved_amount ? Number(decision.approved_amount) : undefined,
        interest_rate: decision.interest_rate ? Number(decision.interest_rate) : undefined,
      });
      toast.success(`Loan ${decision.status}`);
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-primary font-semibold">Applications</div>
          <h1 className="font-display text-3xl mt-2">Loan Applications</h1>
        </div>
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>
            {FILTERS.map((f) => <TabsTrigger key={f.label} value={f.k} data-testid={`admin-filter-${f.label.toLowerCase()}`}>{f.label}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>ID</TableHead><TableHead>Customer</TableHead><TableHead>Type</TableHead><TableHead>Amount</TableHead><TableHead>Tenure</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No records.</TableCell></TableRow>}
              {rows.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="font-mono text-xs">{l.id.slice(0, 8)}</TableCell>
                  <TableCell>{l.customer?.name}<div className="text-xs text-muted-foreground">{l.customer?.email}</div></TableCell>
                  <TableCell className="capitalize">{l.loan_type.replace("_"," ")}</TableCell>
                  <TableCell>₹{Number(l.amount).toLocaleString("en-IN")}</TableCell>
                  <TableCell>{l.tenure_months} mo</TableCell>
                  <TableCell><span className="text-xs px-2 py-1 rounded-full bg-muted">{l.status}</span></TableCell>
                  <TableCell>{new Date(l.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" onClick={() => { setCurrent(l); setDecision({ ...decision, approved_amount: l.amount, interest_rate: l.interest_rate }); setOpen(true); }} data-testid={`admin-decide-${l.id}`}>Decide</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Decide on application</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Decision</Label>
                <select className="w-full h-10 px-3 rounded-md border border-input bg-background" value={decision.status} onChange={(e) => setDecision({ ...decision, status: e.target.value })} data-testid="admin-decision-status">
                  <option value="approved">Approve</option><option value="rejected">Reject</option><option value="disbursed">Mark disbursed</option>
                </select>
              </div>
              <div><Label>Approved amount (₹)</Label><Input value={decision.approved_amount} onChange={(e) => setDecision({ ...decision, approved_amount: e.target.value })} data-testid="admin-decision-amount" /></div>
              <div><Label>Interest rate (%)</Label><Input value={decision.interest_rate} onChange={(e) => setDecision({ ...decision, interest_rate: e.target.value })} /></div>
              <div><Label>Note</Label><Input value={decision.reason} onChange={(e) => setDecision({ ...decision, reason: e.target.value })} /></div>
              <Button onClick={decide} className="w-full rounded-full brand-gradient text-white" data-testid="admin-decision-submit">Save decision</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
